import { apiClient } from "../../../config/axios.config";
import type { PaginationResponse, Response } from "../../../types/api.types";
import type { quotationCopyForm } from "../dtos/request/quotation.request";
import type { getStoreByOwnerIdForm, getStoreForm, matchStoreForm, newStoreForm, getStoreDateLogForm, StoreBanForm, StoreDensityForm, storeForm, StoreUseDateForm } from "../dtos/request/store.request";
import type { StoreDateLogItem, StoreDetailResponse, StoreResponse } from "../dtos/response/store.response";

const model = "/v1/store";

/**
 * @description 지점 상세 정보 조회 API
 * @param id
 * @returns
 */
export const getStoreDetail = async (id: string): Promise<Response<StoreDetailResponse>> => {
  const { data } = await apiClient.get(`${model}/${id}`);
  return data;
};

/**
 * @description 매장 리스트 조회 API
 * @param query
 * @returns
 */
export const getStoreList = async (query: getStoreForm): Promise<PaginationResponse<StoreResponse>> => {
  const { data } = await apiClient.get(model, { params: query });
  return data;
};

export const createStore = async (form: newStoreForm): Promise<Response> => {
  const { data } = await apiClient.post(model, {}, { params: form });
  return data;
}

export const matchStore = async (form: matchStoreForm) => {
  const { data } = await apiClient.patch(
    `${model}/match/${form.id}`,
    form.users
  );
  return data;
};

export const patchStore = async ({
  id,
  form,
}: {
  id: string;
  form: storeForm;
}) => {
  const { data } = await apiClient.patch(`${model}/${id}`, form);
  return data;
};

export const unmatchStore = async (form: matchStoreForm) => {
  const { data } = await apiClient.delete(`${model}/unmatch/${form.id}`, {
    data: form.users,
  } as any);
  return data;
};

export const getStoreByOwnerId = async (form: getStoreByOwnerIdForm): Promise<PaginationResponse<StoreResponse>> => {
  const { data } = await apiClient.get(`${model}/ownerId/${form.ownerId}`, { params: form });
  return data;
}

export const postCopyQuotation = async (form: quotationCopyForm): Promise<Response<unknown>> => {
  const { data } = await apiClient.post(`/v1/copyQuotation`, form);
  return data;
}

export const patchStoreUseDate = async (form: StoreUseDateForm): Promise<Response> => {
  const { data } = await apiClient.patch(`${model}/date/${form.id}`, {}, { params: { startDate: form.startDate, endDate: form.endDate } });
  return data;
}

/** 지점 정지 — PATCH /v1/store/ban/{id} (오늘부터 day 일) */
export const patchStoreBan = async ({ id, day, reason }: StoreBanForm): Promise<Response<StoreDetailResponse>> => {
  const { data } = await apiClient.patch(`${model}/ban/${id}`, {}, {
    params: { day, reason },
  });
  return data;
}

/** 지점 정지 해제 — PATCH /v1/store/ban/{id}/revert (정지 기한만 해제) */
export const patchStoreBanRevert = async (id: number | string): Promise<Response<StoreDetailResponse>> => {
  const { data } = await apiClient.patch(`${model}/ban/${id}/revert`);
  return data;
}

/** 매장 기간 변경 로그 — GET /v1/store/log/date/{storeId} */
export const getStoreDateLog = async (id: string | number, query: getStoreDateLogForm): Promise<PaginationResponse<StoreDateLogItem>> => {
  const { data } = await apiClient.get(`${model}/log/date/${id}`, { params: query });
  return data;
}

export const patchStoreDensity = async (form: StoreDensityForm): Promise<Response> => {
  const { data } = await apiClient.patch(`${model}/density/${form.id}`, {}, { params: { density: form.density } });
  return data;
}

export const postPriorAuthorization = async (file: File, storeId: string): Promise<Response> => {
  const formData = new FormData();
  formData.append("multipartFile", file);

  const { data } = await apiClient.post(`${model}/priorAuthorization/${storeId}`, formData, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
}

export const deletePriorAuthorization = async (storeId: string): Promise<Response> => {
  const { data } = await apiClient.delete(`${model}/priorAuthorization/${storeId}`);
  return data;
}